import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { Loader2, Save, Sparkles, Heart } from "lucide-react";

// ── Types ────────────────────────────────────────────────────────────────────

type YasamYoluRow = {
  id?: string;
  sayi_a: number;
  sayi_b: number;
  uyum_ozeti: string;
  guclu_yanlar: string;
  zorluklar: string;
  iliski_dinamigi: string;
  tavsiyeler: string;
  uyum_yuzdesi: number;
  [key: string]: any;
};

type OrtakYasamYoluRow = {
  id?: string;
  sayi: number;
  iliski_enerjisi: string;
  guclu_yanlar: string;
  zorluklar: string;
  tavsiyeler: string;
  [key: string]: any;
};

type StresSayisiRow = {
  id?: string;
  sayi: number;
  gerilim_alanlari: string;
  iyilestirme_yollari: string;
  [key: string]: any;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fieldKey(base: string, lang: string): string {
  return lang === "tr" ? base : `${base}_${lang}`;
}

function rowText(row: any, keys: string[], lang: string): string {
  return keys.map(k => String(row[fieldKey(k, lang)] ?? row[k] ?? "")).join(" ").toLowerCase();
}

// ── Empty factories ───────────────────────────────────────────────────────────

const emptyYasamYolu = (a: number, b: number): YasamYoluRow => ({
  sayi_a: a, sayi_b: b, uyum_ozeti: "", guclu_yanlar: "", zorluklar: "",
  iliski_dinamigi: "", tavsiyeler: "", uyum_yuzdesi: 60,
});

const emptyOrtak = (sayi: number): OrtakYasamYoluRow => ({
  sayi, iliski_enerjisi: "", guclu_yanlar: "", zorluklar: "", tavsiyeler: "",
});

const emptyStres = (sayi: number): StresSayisiRow => ({
  sayi, gerilim_alanlari: "", iyilestirme_yollari: "",
});

// ── API helpers ───────────────────────────────────────────────────────────────

async function fetchAll<T>(path: string): Promise<T[]> {
  const res = await adminFetch(path);
  if (!res.ok) throw new Error("Veri alinamadi");
  return res.json();
}

async function saveRow(path: string, data: unknown): Promise<void> {
  const res = await adminFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as any;
    throw new Error(err?.error ?? "Kaydedilemedi");
  }
}

async function generateRow(path: string, body: unknown): Promise<any> {
  const res = await adminFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as any;
    throw new Error(err?.error ?? "AI uretimi basarisiz");
  }
  return res.json();
}

// ── Lang tabs ─────────────────────────────────────────────────────────────────

type Lang = "tr" | "en" | "es";

const LANG_OPTIONS: { value: Lang; label: string }[] = [
  { value: "tr", label: "TR Turkce" },
  { value: "en", label: "EN English" },
  { value: "es", label: "ES Espanol" },
];

function LangTabs({ selected, onChange }: { selected: Lang; onChange: (l: Lang) => void }) {
  return (
    <div className="flex gap-1 px-6 pt-3 shrink-0">
      {LANG_OPTIONS.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={selected === opt.value ? { backgroundColor: "#674bb5", color: "#fff", borderColor: "#674bb5" } : {}}
          className={cn(
            "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
            selected === opt.value ? "" : "border-slate-700 bg-slate-800 text-slate-400 hover:bg-slate-700"
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Search input ──────────────────────────────────────────────────────────────

function SearchInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative mb-3">
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Sayi veya icerik ara..."
        className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 outline-none focus:ring-1 focus:ring-violet-500 pr-6"
      />
      {value && (
        <button
          onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-100 text-xs"
          aria-label="Temizle"
        >
          x
        </button>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

type StyleArgs = { inputCls: string; textareaCls: string; labelCls: string; isDark: boolean };

function Grid9x9({
  filledSet,
  selected,
  onSelect,
  loading,
  searchActive,
  matchSet,
}: {
  filledSet: Set<string>;
  selected: { a: number; b: number } | null;
  onSelect: (a: number, b: number) => void;
  loading: boolean;
  searchActive?: boolean;
  matchSet?: Set<string>;
}) {
  if (loading) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Yukleniyor...</div>;
  return (
    <table className="border-collapse text-xs">
      <thead>
        <tr>
          <th className="w-8 pb-1 text-slate-500">A\B</th>
          {Array.from({ length: 9 }, (_, i) => (
            <th key={i} className="w-8 pb-1 text-center text-slate-400">{i + 1}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 9 }, (_, ri) => (
          <tr key={ri}>
            <td className="pr-1 text-right text-slate-400 font-medium">{ri + 1}</td>
            {Array.from({ length: 9 }, (_, ci) => {
              const a = ri + 1, b = ci + 1;
              const key = `${a}-${b}`;
              const filled = filledSet.has(key);
              const isSel = selected?.a === a && selected?.b === b;
              const dimmed = searchActive && matchSet && !matchSet.has(key);
              return (
                <td key={ci} className="p-0.5">
                  <button
                    onClick={() => onSelect(a, b)}
                    className={cn(
                      "w-7 h-7 rounded text-[10px] font-bold transition-all",
                      isSel ? "ring-2 ring-violet-400 scale-110" : "",
                      dimmed ? "opacity-25" : "",
                      filled ? "bg-emerald-600 text-white hover:bg-emerald-500" : "bg-slate-700 text-slate-400 hover:bg-slate-600"
                    )}
                  >
                    {a}/{b}
                  </button>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function List9({
  filledSet,
  selected,
  onSelect,
  loading,
  searchActive,
  matchSet,
}: {
  filledSet: Set<string>;
  selected: number | null;
  onSelect: (n: number) => void;
  loading: boolean;
  searchActive?: boolean;
  matchSet?: Set<string>;
}) {
  if (loading) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Yukleniyor...</div>;
  return (
    <div className="flex flex-col gap-1.5">
      {Array.from({ length: 9 }, (_, i) => {
        const n = i + 1;
        const key = String(n);
        const filled = filledSet.has(key);
        const isSel = selected === n;
        const dimmed = searchActive && matchSet && !matchSet.has(key);
        return (
          <button
            key={n}
            onClick={() => onSelect(n)}
            className={cn(
              "w-full rounded-lg px-4 py-2 text-sm font-bold text-left transition-all",
              isSel ? "ring-2 ring-violet-400" : "",
              dimmed ? "opacity-25" : "",
              filled ? "bg-emerald-600 text-white hover:bg-emerald-500" : "bg-slate-700 text-slate-400 hover:bg-slate-600"
            )}
          >
            Sayi {n}
          </button>
        );
      })}
    </div>
  );
}

// ── Tab: Yasam Yolu Uyumu ─────────────────────────────────────────────────────

function TabYasamYolu({ s, lang }: { s: StyleArgs; lang: Lang }) {
  const [allData, setAllData] = useState<YasamYoluRow[]>([]);
  const [loadingAll, setLoadingAll] = useState(true);
  const [selected, setSelected] = useState<{ a: number; b: number } | null>(null);
  const [form, setForm] = useState<YasamYoluRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ completed: number; total: number; errors: string[] } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchAll<YasamYoluRow>("/api/admin/numeroloji/iliski-yasam-yolu")
      .then(setAllData).catch(() => setAllData([]))
      .finally(() => setLoadingAll(false));
  }, []);

  const filledSet = new Set(allData.map(r => `${r.sayi_a}-${r.sayi_b}`));

  const TEXT_KEYS = ["uyum_ozeti", "guclu_yanlar", "zorluklar", "iliski_dinamigi", "tavsiyeler"];
  const q = search.trim().toLowerCase();
  const matchSet = q ? new Set(allData.filter(r => {
    const nums = `${r.sayi_a} ${r.sayi_b}`;
    return nums.includes(q) || rowText(r, TEXT_KEYS, lang).includes(q);
  }).map(r => `${r.sayi_a}-${r.sayi_b}`)) : undefined;

  const handleSelect = (a: number, b: number) => {
    setSelected({ a, b });
    const ex = allData.find(r => r.sayi_a === a && r.sayi_b === b);
    setForm(ex ? { ...ex } : emptyYasamYolu(a, b));
    setMsg(null);
  };

  const setF = (key: string, val: any) => setForm(f => f ? { ...f, [key]: val } : f);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true); setMsg(null);
    try {
      await saveRow("/api/admin/numeroloji/iliski-yasam-yolu", form);
      setAllData(prev => {
        const idx = prev.findIndex(r => r.sayi_a === form.sayi_a && r.sayi_b === form.sayi_b);
        if (idx >= 0) { const n = [...prev]; n[idx] = form; return n; }
        return [...prev, form];
      });
      setMsg("Kaydedildi");
    } catch (e: any) { setMsg(`Hata: ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleGenerate = async () => {
    if (!selected) return;
    setGenerating(true); setMsg(null);
    try {
      const gen = await generateRow("/api/admin/numeroloji/iliski-yasam-yolu/generate", { sayi_a: selected.a, sayi_b: selected.b });
      setForm(gen);
      setMsg("AI icerigi uretildi -- kaydetmeyi unutmayin.");
    } catch (e: any) { setMsg(`AI Hatasi: ${e.message}`); }
    finally { setGenerating(false); }
  };

  const handleBulkGenerate = async () => {
    setBulkGenerating(true); setBulkProgress(null); setMsg(null);
    try {
      const res = await adminFetch("/api/admin/numeroloji/iliski-yasam-yolu/bulk-generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const result = await res.json();
      setBulkProgress(result);
      const fresh = await fetchAll<YasamYoluRow>("/api/admin/numeroloji/iliski-yasam-yolu");
      setAllData(fresh);
      setMsg(`Tamamlandi: ${result.completed}/${result.total}`);
    } catch (e: any) { setMsg(`Hata: ${e.message}`); }
    finally { setBulkGenerating(false); }
  };

  const fields = [
    { key: "uyum_ozeti", label: "Uyum Ozeti", rows: 4 },
    { key: "guclu_yanlar", label: "Guclu Yanlar", rows: 4 },
    { key: "zorluklar", label: "Zorluklar", rows: 4 },
    { key: "iliski_dinamigi", label: "Iliski Dinamigi", rows: 4 },
    { key: "tavsiyeler", label: "Tavsiyeler", rows: 4 },
  ];

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="w-[340px] shrink-0 border-r border-slate-800 p-4 overflow-auto">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs text-slate-400">{allData.length}/81 dolu</span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleBulkGenerate}
            disabled={bulkGenerating || generating}
            className="gap-1.5 border-violet-600 text-violet-400 hover:bg-violet-900/30 text-xs px-2 py-1 h-auto"
          >
            {bulkGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {bulkGenerating ? "Uretiliyor..." : "Tumunu Uret"}
          </Button>
        </div>
        {bulkProgress && (
          <div className="mb-2 text-xs">
            <span className="text-emerald-400">{bulkProgress.completed} / {bulkProgress.total} tamamlandi</span>
            {bulkProgress.errors.length > 0 && (
              <div className="mt-1 text-red-400 space-y-0.5">
                {bulkProgress.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
          </div>
        )}
        <SearchInput value={search} onChange={setSearch} />
        <Grid9x9 filledSet={filledSet} selected={selected} onSelect={handleSelect} loading={loadingAll} searchActive={!!q} matchSet={matchSet} />
      </div>
      <div className="flex-1 overflow-auto p-6">
        {!form ? (
          <div className="flex h-full items-center justify-center text-slate-500 text-center">
            <p>Sol taraftan bir kombinasyon secin</p>
          </div>
        ) : (
          <div className="max-w-2xl space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold">Sayi {form.sayi_a} x Sayi {form.sayi_b}</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleGenerate} disabled={generating} className="gap-2 border-violet-600 text-violet-400 hover:bg-violet-900/30">
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  AI ile Uret
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="gap-2 bg-violet-700 hover:bg-violet-600">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Kaydet
                </Button>
              </div>
            </div>
            {msg && <p className={cn("text-sm", msg.startsWith("Hata") || msg.startsWith("AI Hatasi") ? "text-red-400" : "text-emerald-400")}>{msg}</p>}
            {lang === "tr" && (
              <div>
                <label className={s.labelCls}>Uyum Yuzdesi: {form.uyum_yuzdesi}%</label>
                <input type="range" min={0} max={100} value={form.uyum_yuzdesi}
                  onChange={e => setF("uyum_yuzdesi", Number(e.target.value))}
                  className="w-full accent-violet-500" />
              </div>
            )}
            {fields.map(({ key, label, rows }) => {
              const fk = fieldKey(key, lang);
              return (
                <div key={key}>
                  <label className={s.labelCls}>{label} {lang !== "tr" ? `(${lang.toUpperCase()})` : ""}</label>
                  <textarea className={s.textareaCls} rows={rows} value={form[fk] ?? ""} onChange={e => setF(fk, e.target.value)} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab: Ortak Yasam Yolu ─────────────────────────────────────────────────────

function TabOrtakYasamYolu({ s, lang }: { s: StyleArgs; lang: Lang }) {
  const [allData, setAllData] = useState<OrtakYasamYoluRow[]>([]);
  const [loadingAll, setLoadingAll] = useState(true);
  const [selected, setSelected] = useState<number | null>(null);
  const [form, setForm] = useState<OrtakYasamYoluRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ completed: number; total: number; errors: string[] } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchAll<OrtakYasamYoluRow>("/api/admin/numeroloji/iliski-ortak-yasam-yolu")
      .then(setAllData).catch(() => setAllData([]))
      .finally(() => setLoadingAll(false));
  }, []);

  const filledSet = new Set(allData.map(r => String(r.sayi)));

  const TEXT_KEYS = ["iliski_enerjisi", "guclu_yanlar", "zorluklar", "tavsiyeler"];
  const q = search.trim().toLowerCase();
  const matchSet = q ? new Set(allData.filter(r => {
    return String(r.sayi).includes(q) || rowText(r, TEXT_KEYS, lang).includes(q);
  }).map(r => String(r.sayi))) : undefined;

  const handleSelect = (n: number) => {
    setSelected(n);
    const ex = allData.find(r => r.sayi === n);
    setForm(ex ? { ...ex } : emptyOrtak(n));
    setMsg(null);
  };

  const setF = (key: string, val: any) => setForm(f => f ? { ...f, [key]: val } : f);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true); setMsg(null);
    try {
      await saveRow("/api/admin/numeroloji/iliski-ortak-yasam-yolu", form);
      setAllData(prev => {
        const idx = prev.findIndex(r => r.sayi === form.sayi);
        if (idx >= 0) { const n = [...prev]; n[idx] = form; return n; }
        return [...prev, form];
      });
      setMsg("Kaydedildi");
    } catch (e: any) { setMsg(`Hata: ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleGenerate = async () => {
    if (!selected) return;
    setGenerating(true); setMsg(null);
    try {
      const gen = await generateRow("/api/admin/numeroloji/iliski-ortak-yasam-yolu/generate", { sayi: selected });
      setForm(gen);
      setMsg("AI icerigi uretildi -- kaydetmeyi unutmayin.");
    } catch (e: any) { setMsg(`AI Hatasi: ${e.message}`); }
    finally { setGenerating(false); }
  };

  const handleBulkGenerate = async () => {
    setBulkGenerating(true); setBulkProgress(null); setMsg(null);
    try {
      const res = await adminFetch("/api/admin/numeroloji/iliski-ortak-yasam-yolu/bulk-generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const result = await res.json();
      setBulkProgress(result);
      const fresh = await fetchAll<OrtakYasamYoluRow>("/api/admin/numeroloji/iliski-ortak-yasam-yolu");
      setAllData(fresh);
      setMsg(`Tamamlandi: ${result.completed}/${result.total}`);
    } catch (e: any) { setMsg(`Hata: ${e.message}`); }
    finally { setBulkGenerating(false); }
  };

  const fields = [
    { key: "iliski_enerjisi", label: "Iliski Enerjisi", rows: 4 },
    { key: "guclu_yanlar", label: "Guclu Yanlar", rows: 4 },
    { key: "zorluklar", label: "Zorluklar", rows: 4 },
    { key: "tavsiyeler", label: "Tavsiyeler", rows: 4 },
  ];

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="w-[200px] shrink-0 border-r border-slate-800 p-4 overflow-auto">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs text-slate-400">{allData.length}/9 dolu</span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleBulkGenerate}
            disabled={bulkGenerating || generating}
            className="gap-1.5 border-violet-600 text-violet-400 hover:bg-violet-900/30 text-xs px-2 py-1 h-auto"
          >
            {bulkGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {bulkGenerating ? "Uretiliyor..." : "Tumunu Uret"}
          </Button>
        </div>
        {bulkProgress && (
          <div className="mb-2 text-xs">
            <span className="text-emerald-400">{bulkProgress.completed} / {bulkProgress.total} tamamlandi</span>
            {bulkProgress.errors.length > 0 && (
              <div className="mt-1 text-red-400 space-y-0.5">
                {bulkProgress.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
          </div>
        )}
        <SearchInput value={search} onChange={setSearch} />
        <List9 filledSet={filledSet} selected={selected} onSelect={handleSelect} loading={loadingAll} searchActive={!!q} matchSet={matchSet} />
      </div>
      <div className="flex-1 overflow-auto p-6">
        {!form ? (
          <div className="flex h-full items-center justify-center text-slate-500"><p>Sol taraftan bir sayi secin</p></div>
        ) : (
          <div className="max-w-2xl space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold">Ortak Yasam Yolu: Sayi {form.sayi}</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleGenerate} disabled={generating} className="gap-2 border-violet-600 text-violet-400 hover:bg-violet-900/30">
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  AI ile Uret
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="gap-2 bg-violet-700 hover:bg-violet-600">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Kaydet
                </Button>
              </div>
            </div>
            {msg && <p className={cn("text-sm", msg.startsWith("Hata") || msg.startsWith("AI Hatasi") ? "text-red-400" : "text-emerald-400")}>{msg}</p>}
            {fields.map(({ key, label, rows }) => {
              const fk = fieldKey(key, lang);
              return (
                <div key={key}>
                  <label className={s.labelCls}>{label} {lang !== "tr" ? `(${lang.toUpperCase()})` : ""}</label>
                  <textarea className={s.textareaCls} rows={rows} value={form[fk] ?? ""} onChange={e => setF(fk, e.target.value)} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab: Stres Sayisi ─────────────────────────────────────────────────────────

function TabStresSayisi({ s, lang }: { s: StyleArgs; lang: Lang }) {
  const [allData, setAllData] = useState<StresSayisiRow[]>([]);
  const [loadingAll, setLoadingAll] = useState(true);
  const [selected, setSelected] = useState<number | null>(null);
  const [form, setForm] = useState<StresSayisiRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ completed: number; total: number; errors: string[] } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchAll<StresSayisiRow>("/api/admin/numeroloji/iliski-stres-sayisi")
      .then(setAllData).catch(() => setAllData([]))
      .finally(() => setLoadingAll(false));
  }, []);

  const filledSet = new Set(allData.map(r => String(r.sayi)));

  const TEXT_KEYS = ["gerilim_alanlari", "iyilestirme_yollari"];
  const q = search.trim().toLowerCase();
  const matchSet = q ? new Set(allData.filter(r => {
    return String(r.sayi).includes(q) || rowText(r, TEXT_KEYS, lang).includes(q);
  }).map(r => String(r.sayi))) : undefined;

  const handleSelect = (n: number) => {
    setSelected(n);
    const ex = allData.find(r => r.sayi === n);
    setForm(ex ? { ...ex } : emptyStres(n));
    setMsg(null);
  };

  const setF = (key: string, val: any) => setForm(f => f ? { ...f, [key]: val } : f);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true); setMsg(null);
    try {
      await saveRow("/api/admin/numeroloji/iliski-stres-sayisi", form);
      setAllData(prev => {
        const idx = prev.findIndex(r => r.sayi === form.sayi);
        if (idx >= 0) { const n = [...prev]; n[idx] = form; return n; }
        return [...prev, form];
      });
      setMsg("Kaydedildi");
    } catch (e: any) { setMsg(`Hata: ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleGenerate = async () => {
    if (!selected) return;
    setGenerating(true); setMsg(null);
    try {
      const gen = await generateRow("/api/admin/numeroloji/iliski-stres-sayisi/generate", { sayi: selected });
      setForm(gen);
      setMsg("AI icerigi uretildi -- kaydetmeyi unutmayin.");
    } catch (e: any) { setMsg(`AI Hatasi: ${e.message}`); }
    finally { setGenerating(false); }
  };

  const handleBulkGenerate = async () => {
    setBulkGenerating(true); setBulkProgress(null); setMsg(null);
    try {
      const res = await adminFetch("/api/admin/numeroloji/iliski-stres-sayisi/bulk-generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const result = await res.json();
      setBulkProgress(result);
      const fresh = await fetchAll<StresSayisiRow>("/api/admin/numeroloji/iliski-stres-sayisi");
      setAllData(fresh);
      setMsg(`Tamamlandi: ${result.completed}/${result.total}`);
    } catch (e: any) { setMsg(`Hata: ${e.message}`); }
    finally { setBulkGenerating(false); }
  };

  const fields = [
    { key: "gerilim_alanlari", label: "Gerilim Alanlari", rows: 5 },
    { key: "iyilestirme_yollari", label: "Iyilestirme Yollari", rows: 5 },
  ];

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="w-[200px] shrink-0 border-r border-slate-800 p-4 overflow-auto">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs text-slate-400">{allData.length}/10 dolu</span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleBulkGenerate}
            disabled={bulkGenerating || generating}
            className="gap-1.5 border-violet-600 text-violet-400 hover:bg-violet-900/30 text-xs px-2 py-1 h-auto"
          >
            {bulkGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {bulkGenerating ? "Uretiliyor..." : "Tumunu Uret"}
          </Button>
        </div>
        {bulkProgress && (
          <div className="mb-2 text-xs">
            <span className="text-emerald-400">{bulkProgress.completed} / {bulkProgress.total} tamamlandi</span>
            {bulkProgress.errors.length > 0 && (
              <div className="mt-1 text-red-400 space-y-0.5">
                {bulkProgress.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
          </div>
        )}
        <SearchInput value={search} onChange={setSearch} />
        <List9 filledSet={filledSet} selected={selected} onSelect={handleSelect} loading={loadingAll} searchActive={!!q} matchSet={matchSet} />
      </div>
      <div className="flex-1 overflow-auto p-6">
        {!form ? (
          <div className="flex h-full items-center justify-center text-slate-500"><p>Sol taraftan bir sayi secin</p></div>
        ) : (
          <div className="max-w-2xl space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold">Stres Sayisi: {form.sayi}</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleGenerate} disabled={generating} className="gap-2 border-violet-600 text-violet-400 hover:bg-violet-900/30">
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  AI ile Uret
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="gap-2 bg-violet-700 hover:bg-violet-600">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Kaydet
                </Button>
              </div>
            </div>
            {msg && <p className={cn("text-sm", msg.startsWith("Hata") || msg.startsWith("AI Hatasi") ? "text-red-400" : "text-emerald-400")}>{msg}</p>}
            {fields.map(({ key, label, rows }) => {
              const fk = fieldKey(key, lang);
              return (
                <div key={key}>
                  <label className={s.labelCls}>{label} {lang !== "tr" ? `(${lang.toUpperCase()})` : ""}</label>
                  <textarea className={s.textareaCls} rows={rows} value={form[fk] ?? ""} onChange={e => setF(fk, e.target.value)} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab: Generic 81-combo with single "yorum" field ───────────────────────────

function Tab81Yorum({
  s,
  lang,
  apiPath,
  label,
}: {
  s: StyleArgs;
  lang: Lang;
  apiPath: string;
  label: string;
}) {
  type Row = { id?: string; sayi_a: number; sayi_b: number; yorum: string; [key: string]: any };
  const [allData, setAllData] = useState<Row[]>([]);
  const [loadingAll, setLoadingAll] = useState(true);
  const [selected, setSelected] = useState<{ a: number; b: number } | null>(null);
  const [form, setForm] = useState<Row | null>(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ completed: number; total: number; errors: string[] } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchAll<Row>(apiPath)
      .then(setAllData).catch(() => setAllData([]))
      .finally(() => setLoadingAll(false));
  }, [apiPath]);

  const filledSet = new Set(allData.map(r => `${r.sayi_a}-${r.sayi_b}`));

  const q = search.trim().toLowerCase();
  const matchSet = q ? new Set(allData.filter(r => {
    const nums = `${r.sayi_a} ${r.sayi_b}`;
    const text = String(r[fieldKey("yorum", lang)] ?? r.yorum ?? "").toLowerCase();
    return nums.includes(q) || text.includes(q);
  }).map(r => `${r.sayi_a}-${r.sayi_b}`)) : undefined;

  const handleSelect = (a: number, b: number) => {
    setSelected({ a, b });
    const ex = allData.find(r => r.sayi_a === a && r.sayi_b === b);
    setForm(ex ? { ...ex } : { sayi_a: a, sayi_b: b, yorum: "" });
    setMsg(null);
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true); setMsg(null);
    try {
      await saveRow(apiPath, form);
      setAllData(prev => {
        const idx = prev.findIndex(r => r.sayi_a === form.sayi_a && r.sayi_b === form.sayi_b);
        if (idx >= 0) { const n = [...prev]; n[idx] = form; return n; }
        return [...prev, form];
      });
      setMsg("Kaydedildi");
    } catch (e: any) { setMsg(`Hata: ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleGenerate = async () => {
    if (!selected) return;
    setGenerating(true); setMsg(null);
    try {
      const gen = await generateRow(`${apiPath}/generate`, { sayi_a: selected.a, sayi_b: selected.b });
      setForm(gen);
      setMsg("AI icerigi uretildi -- kaydetmeyi unutmayin.");
    } catch (e: any) { setMsg(`AI Hatasi: ${e.message}`); }
    finally { setGenerating(false); }
  };

  const handleBulkGenerate = async () => {
    setBulkGenerating(true); setBulkProgress(null); setMsg(null);
    try {
      const res = await adminFetch(`${apiPath}/bulk-generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const result = await res.json();
      setBulkProgress(result);
      const fresh = await fetchAll<Row>(apiPath);
      setAllData(fresh);
      setMsg(`Tamamlandi: ${result.completed}/${result.total}`);
    } catch (e: any) { setMsg(`Hata: ${e.message}`); }
    finally { setBulkGenerating(false); }
  };

  const yorumKey = fieldKey("yorum", lang);

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="w-[340px] shrink-0 border-r border-slate-800 p-4 overflow-auto">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs text-slate-400">{allData.length}/81 dolu</span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleBulkGenerate}
            disabled={bulkGenerating || generating}
            className="gap-1.5 border-violet-600 text-violet-400 hover:bg-violet-900/30 text-xs px-2 py-1 h-auto"
          >
            {bulkGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {bulkGenerating ? "Uretiliyor..." : "Tumunu Uret"}
          </Button>
        </div>
        {bulkProgress && (
          <div className="mb-2 text-xs">
            <span className="text-emerald-400">{bulkProgress.completed} / {bulkProgress.total} tamamlandi</span>
            {bulkProgress.errors.length > 0 && (
              <div className="mt-1 text-red-400 space-y-0.5">
                {bulkProgress.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
          </div>
        )}
        <SearchInput value={search} onChange={setSearch} />
        <Grid9x9 filledSet={filledSet} selected={selected} onSelect={handleSelect} loading={loadingAll} searchActive={!!q} matchSet={matchSet} />
      </div>
      <div className="flex-1 overflow-auto p-6">
        {!form ? (
          <div className="flex h-full items-center justify-center text-slate-500"><p>Sol taraftan bir kombinasyon secin</p></div>
        ) : (
          <div className="max-w-2xl space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold">{label}: Sayi {form.sayi_a} x Sayi {form.sayi_b}</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleGenerate} disabled={generating} className="gap-2 border-violet-600 text-violet-400 hover:bg-violet-900/30">
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  AI ile Uret
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="gap-2 bg-violet-700 hover:bg-violet-600">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Kaydet
                </Button>
              </div>
            </div>
            {msg && <p className={cn("text-sm", msg.startsWith("Hata") || msg.startsWith("AI Hatasi") ? "text-red-400" : "text-emerald-400")}>{msg}</p>}
            <div>
              <label className={s.labelCls}>Yorum {lang !== "tr" ? `(${lang.toUpperCase()})` : ""}</label>
              <textarea
                className={s.textareaCls}
                rows={10}
                value={form[yorumKey] ?? ""}
                onChange={e => setForm(f => f ? { ...f, [yorumKey]: e.target.value } : f)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

type TabId = "yasam-yolu" | "ortak-yasam" | "stres" | "tutum" | "olgunluk";

const TABS: { id: TabId; label: string }[] = [
  { id: "yasam-yolu", label: "Yasam Yolu Uyumu" },
  { id: "ortak-yasam", label: "Ortak Yasam Yolu" },
  { id: "stres", label: "Stres Sayisi" },
  { id: "tutum", label: "Tutum Uyumu" },
  { id: "olgunluk", label: "Olgunluk Uyumu" },
];

export default function AdminNumerolojiIliskiAnalizi() {
  const { sessionQuery, loginMutation } = useAdminSession();
  const authenticated = Boolean(sessionQuery.data?.authenticated);
  const configured = Boolean(sessionQuery.data?.configured);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [activeTab, setActiveTab] = useState<TabId>("yasam-yolu");
  const [selectedLang, setSelectedLang] = useState<Lang>("tr");

  if (!authenticated) return (
    <AdminLoginCard
      configured={configured}
      onLogin={(args) => loginMutation.mutate(args)}
      isLoading={loginMutation.isPending}
      error={loginMutation.error?.message}
    />
  );

  const inputCls = cn("w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-500",
    isDark ? "border-slate-700 bg-slate-800 text-slate-100 placeholder:text-slate-500" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400");
  const textareaCls = cn(inputCls, "resize-none");
  const labelCls = cn("block text-xs font-semibold uppercase tracking-wide mb-1", isDark ? "text-slate-400" : "text-slate-500");
  const s: StyleArgs = { inputCls, textareaCls, labelCls, isDark };

  return (
    <div className={cn("flex min-h-screen", isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900")}>
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => {}} />
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 border-b px-6 py-4 border-slate-800">
          <NumerolojiAdminMobileSidebar mode="links" isDark={isDark} onToggleTheme={() => {}} />
          <Heart className="h-5 w-5 text-violet-400" />
          <h1 className="text-lg font-bold">Iliski Analizi</h1>
        </div>

        {/* Lang tabs */}
        <LangTabs selected={selectedLang} onChange={setSelectedLang} />

        {/* Section tab bar */}
        <div className="flex gap-1 border-b border-slate-800 px-4 pt-3 overflow-x-auto shrink-0">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "whitespace-nowrap rounded-t-lg px-4 py-2 text-sm font-medium transition-colors",
                activeTab === tab.id
                  ? "bg-violet-700 text-white"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex flex-1 overflow-hidden">
          {activeTab === "yasam-yolu" && <TabYasamYolu s={s} lang={selectedLang} />}
          {activeTab === "ortak-yasam" && <TabOrtakYasamYolu s={s} lang={selectedLang} />}
          {activeTab === "stres" && <TabStresSayisi s={s} lang={selectedLang} />}
          {activeTab === "tutum" && (
            <Tab81Yorum s={s} lang={selectedLang} apiPath="/api/admin/numeroloji/iliski-tutum-uyum" label="Tutum Uyumu" />
          )}
          {activeTab === "olgunluk" && (
            <Tab81Yorum s={s} lang={selectedLang} apiPath="/api/admin/numeroloji/iliski-olgunluk-uyum" label="Olgunluk Uyumu" />
          )}
        </div>
      </div>
    </div>
  );
}
